import React, { useState, useRef, useEffect } from 'react';
import GenericCard from '@ui-components/Card/GenericCard';
import { GenericTable, GenericTableColumn } from '@ui-components/Table/GenericTable';
import GenericSearchbar, { ParsedFilter } from '@ui-components/Searchbar/GenericSearchbar';
import { DataStateView } from 'kontrolEdge/components/Backbone/BackboneDetails/StateViews';
import { TableSkeleton } from '@ui-components/Skeleton/GenericSkeleton';
import { useAppSelector } from '@redux/hooks';
import { getReflectorCloudElements } from '../request/request';
import { getClusterCloudElementsPaginated } from '../Cluster/request/request';
import { filterCloudElements } from '@shared/components/utils/searchUtils';
import {
  CLOUD_ELEMENTS_EMPTY_TITLE,
  CLOUD_ELEMENTS_EMPTY_DESCRIPTION_REFLECTOR,
  CLOUD_ELEMENTS_EMPTY_DESCRIPTION_CLUSTER,
  CLOUD_ELEMENTS_ERROR_TITLE,
} from './CloudElementsData';
import { RootState } from '@redux/store/store';
import { getTableBodyMaxHeight } from '../../../helpers/tableViewport';

/** Row type for cloud elements table (compatible with Reflector and Cluster column defs) */
export interface CloudElementRow {
  id?: number;
  elementType?: string;
  elementId?: string;
  vpcId?: string | null;
  region?: string;
  tags?: string | Record<string, unknown>;
  discoveredAt?: string;
  [key: string]: unknown;
}

/** API response object shape (when response is not a raw array) */
interface CloudElementsApiResponseObject {
  data?: { elements?: CloudElementRow[]; total_elements?: number } | CloudElementRow[];
  cloudElements?: CloudElementRow[];
  pagination?: { totalRecords?: number; total?: number };
  total?: number;
  count?: number;
}

/** API response shape for cloud elements (reflector or cluster) */
type CloudElementsApiResponse = CloudElementRow[] | CloudElementsApiResponseObject;

export interface CloudElementsPageProps {
  /** Table column definitions (from ReflectorDetailsData or ClusterDetailsData) */
  columns: GenericTableColumn<Record<string, unknown>>[];
  /** Suggested filter keys for the search bar (e.g. Type, Element ID, Tags) */
  suggestedKeys: string[];
  /** Standalone reflector: fetch elements for this reflector */
  reflectorId?: number;
  /** Cluster: fetch elements for this cluster */
  clusterId?: string;
  getAccessTokenSilently: () => Promise<string>;
  /** Optional callback when user retries after error */
  onRetry?: () => void;
}

const mapKeyToBackend = (uiKey: string): string => {
  const keyMap: { [key: string]: string } = {
    type: 'elementType',
    element: 'elementId',
    'element id': 'elementId',
    vpc: 'vpcId',
    region: 'region',
    tags: 'tags',
    metadata: 'metadata',
    'discovered at': 'discoveredAt',
    'discovered by': 'discoveredBy',
  };
  return keyMap[uiKey.toLowerCase()] || uiKey.toLowerCase().replace(/\s+/g, '');
};

const CloudElementsPage: React.FC<CloudElementsPageProps> = ({
  columns,
  suggestedKeys,
  reflectorId,
  clusterId,
  getAccessTokenSilently,
  onRetry,
}) => {
  const { tenantId } = useAppSelector((state: RootState) => state.selection);

  const [cloudElementsFilters, setCloudElementsFilters] = useState<ParsedFilter[]>([]);
  const [cloudElements, setCloudElements] = useState<CloudElementRow[]>([]);
  const [cloudElementsLoading, setCloudElementsLoading] = useState(false);
  const [cloudElementsError, setCloudElementsError] = useState<string | null>(null);
  const [cloudElementsPage, setCloudElementsPage] = useState(1);
  const [cloudElementsRowsPerPage, setCloudElementsRowsPerPage] = useState(20);
  const [cloudElementsTotalCount, setCloudElementsTotalCount] = useState(0);
  const [cloudElementsRefreshKey, setCloudElementsRefreshKey] = useState(0);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');

  const isFirstLoadRef = useRef<boolean>(true);
  const isFetchingRef = useRef<boolean>(false);
  const lastFetchKeyRef = useRef<string>('');
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;

  const prevDebouncedSearchRef = useRef<string>('');
  const prevFiltersRef = useRef<ParsedFilter[]>([]);

  useEffect(() => {
    const searchChanged =
      prevDebouncedSearchRef.current !== '' &&
      prevDebouncedSearchRef.current !== debouncedSearchValue;
    const filtersChanged =
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(cloudElementsFilters);
    if (searchChanged || filtersChanged) {
      setCloudElementsPage(1);
    }
    prevDebouncedSearchRef.current = debouncedSearchValue;
    prevFiltersRef.current = cloudElementsFilters;
  }, [debouncedSearchValue, cloudElementsFilters]);

  const buildFilterQuery = (): string | undefined => {
    const parts: string[] = [];
    if (debouncedSearchValue.trim()) {
      const isKeyValuePair =
        debouncedSearchValue.includes('=') || debouncedSearchValue.includes(':');
      if (!isKeyValuePair) {
        parts.push(debouncedSearchValue.trim());
      }
    }
    cloudElementsFilters.forEach((filter) => {
      if (filter.key?.trim() && filter.value?.trim()) {
        const backendKey = mapKeyToBackend(filter.key);
        parts.push(`${backendKey}:${filter.value.trim()}`);
      } else if (filter.value?.trim() && !filter.key?.trim()) {
        parts.push(filter.value.trim());
      }
    });
    if (parts.length === 0) return undefined;
    return parts.join(',');
  };

  const shouldCallWithoutFilters = (): boolean => {
    const isFirstLoad = isFirstLoadRef.current;
    const isEmptySearch = !debouncedSearchValue.trim();
    const hasNoFilters = cloudElementsFilters.length === 0;
    return isFirstLoad || (isEmptySearch && hasNoFilters);
  };

  const cloudElementsFiltersKey = JSON.stringify(cloudElementsFilters);
  const entityId = reflectorId ?? clusterId ?? '';

  useEffect(() => {
    const fetchCloudElements = async () => {
      if (!entityId || !tenantId) return;
      if (reflectorId == null && clusterId == null) return;

      const fetchKey = `${entityId}-${tenantId}-${cloudElementsPage}-${cloudElementsRowsPerPage}-${debouncedSearchValue}-${cloudElementsFiltersKey}`;
      if (isFetchingRef.current || lastFetchKeyRef.current === fetchKey) return;

      isFetchingRef.current = true;
      lastFetchKeyRef.current = fetchKey;
      setCloudElementsLoading(true);
      setCloudElementsError(null);

      try {
        const token = await getAccessTokenSilentlyRef.current();
        const shouldCallBase = shouldCallWithoutFilters();
        const filterQuery = shouldCallBase ? undefined : buildFilterQuery();

        if (isFirstLoadRef.current) {
          isFirstLoadRef.current = false;
        }

        let response: { data: CloudElementsApiResponse };
        if (reflectorId != null) {
          response = await getReflectorCloudElements(
            token,
            reflectorId,
            tenantId,
            cloudElementsPage,
            cloudElementsRowsPerPage,
            filterQuery
          );
        } else if (clusterId != null) {
          response = await getClusterCloudElementsPaginated(
            token,
            clusterId,
            tenantId,
            cloudElementsPage,
            cloudElementsRowsPerPage,
            filterQuery
          );
        } else {
          return;
        }

        const responseData = response.data;
        let elementsArray: CloudElementRow[] = [];
        let total = 0;

        if (Array.isArray(responseData)) {
          elementsArray = responseData;
          total = responseData.length;
        } else {
          const obj: CloudElementsApiResponseObject = responseData;
          if (obj?.data && typeof obj.data === 'object' && !Array.isArray(obj.data) && obj.data?.elements && Array.isArray(obj.data.elements)) {
            elementsArray = obj.data.elements;
            total =
              obj.pagination?.totalRecords ??
              obj.pagination?.total ??
              obj.data.total_elements ??
              0;
          } else if (obj?.cloudElements && Array.isArray(obj.cloudElements)) {
            elementsArray = obj.cloudElements;
            total =
              obj.pagination?.totalRecords ??
              obj.pagination?.total ??
              obj.total ??
              0;
          } else if (obj?.data && Array.isArray(obj.data)) {
            elementsArray = obj.data;
            total = obj.total ?? obj.count ?? obj.data.length ?? 0;
          }
        }

        setCloudElements(elementsArray);
        setCloudElementsTotalCount(total);
      } catch (error) {
        console.error('Error fetching cloud elements:', error);
        setCloudElementsError('Failed to load cloud elements. Please try again.');
        setCloudElements([]);
        setCloudElementsTotalCount(0);
      } finally {
        setCloudElementsLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchCloudElements();
  }, [
    reflectorId,
    clusterId,
    tenantId,
    cloudElementsPage,
    cloudElementsRowsPerPage,
    debouncedSearchValue,
    cloudElementsFiltersKey,
    cloudElementsRefreshKey,
  ]);

  const filteredCloudElements = filterCloudElements(cloudElements, cloudElementsFilters);
  const emptyDescription =
    clusterId != null
      ? CLOUD_ELEMENTS_EMPTY_DESCRIPTION_CLUSTER
      : CLOUD_ELEMENTS_EMPTY_DESCRIPTION_REFLECTOR;

  const handleRetry = () => {
    setCloudElementsError(null);
    setCloudElementsRefreshKey((k) => k + 1);
    onRetry?.();
  };

  return (
    <GenericCard isCollapsible={false}>
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold">Cloud Elements Discovered</h2>
        </div>
        <div className="w-full flex-shrink-0 mb-2">
          <GenericSearchbar
            placeholder="Search Cloud Elements..."
            suggestedKeys={suggestedKeys}
            onFiltersChange={setCloudElementsFilters}
            serverSideSearch={true}
            onServerSideSearchChange={setDebouncedSearchValue}
            inputMaxWidth="100%"
          />
        </div>
        <DataStateView
          loading={cloudElementsLoading}
          error={cloudElementsError}
          isEmpty={filteredCloudElements.length === 0}
          onRetry={handleRetry}
          loadingContent={<TableSkeleton columns={6} rows={8} />}
          errorTitle={CLOUD_ELEMENTS_ERROR_TITLE}
          emptyTitle={CLOUD_ELEMENTS_EMPTY_TITLE}
          emptyDescription={emptyDescription}
        >
          <GenericTable
            columns={columns}
            data={filteredCloudElements}
            initialRowsPerPage={cloudElementsRowsPerPage}
            rowsPerPageOptions={[5, 10, 20, 50]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
            maxTableBodyHeight={getTableBodyMaxHeight(340)}
            serverSide={true}
            totalCount={cloudElementsTotalCount}
            currentPage={cloudElementsPage}
            rowsPerPage={cloudElementsRowsPerPage}
            onPageChange={setCloudElementsPage}
            onRowsPerPageChange={setCloudElementsRowsPerPage}
          />
        </DataStateView>
      </div>
    </GenericCard>
  );
};

export default CloudElementsPage;
